import { App } from '../../App';
import { btoa } from 'js-base64';
import { getFetchHeaders } from '../../utils/fetchHeaders';
import { routes } from '../../routes';
import { setShiftedDate, SHIFTED_DATETIME } from '../../utils/shiftedDateTime';
import { useAnalyticsCookies } from '../../hooks/useAnalyticsCookies';
import { useEffect } from 'react';
import { useErrorManager } from 'InternalLibrary';
import { useGetClientApplication } from '../../api';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMaybeNavigateByResolution } from '../../hooks/useMaybeNavigateByResolution';
import IdentifyEvent from '../../hooks/eventCollector/events/IdentifyEvent';
import useClient from '../../hooks/useClient';
import useEventCollector from '../../hooks/eventCollector/useEventCollector';

import { Loading } from 'components';
import dayjs from 'dayjs';
import useAffiliateInfo from '../../hooks/useAffiliateInfo';
import useCrossDomainLoginRedirect from '../../hooks/useCrossDomainLoginRedirect';

export default function OneTimeTokenLogin() {
  const [searchParams] = useSearchParams();
  const { fetch: oneTimeLogin } = App.useApi('token/one-time', 'POST');
  const { fetch: registrationAffiliateLogin } = App.useApi(
    'token/affiliate',
    'POST',
  );
  const navigate = useNavigate();
  const { setError } = useErrorManager();
  const location = useLocation();
  const { fetchClient } = useClient();
  const { fetch: fetchGetApplication } = useGetClientApplication();
  const { maybeRedirectCrossDomain } = useCrossDomainLoginRedirect();
  const { maybeNavigateByResolution } = useMaybeNavigateByResolution();
  const { fetch: fetchUpdateAffiliate } = App.useApi(
    'client/affiliate',
    'POST',
  );

  const { affiliateInfo } = useAffiliateInfo();
  const { sendAnalyticsCookiesIfExists } = useAnalyticsCookies();
  const { identify, reset } = useEventCollector();

  useEffect(() => {
    reset();
    const productNumber = searchParams.get('productNumber');
    if (productNumber) {
      sessionStorage.setItem('productNumberLoginDashboard', productNumber);
    }
    const token = searchParams.get('token');
    const shiftedDateTime = searchParams.get(SHIFTED_DATETIME);

    if (shiftedDateTime) {
      const shiftedDateTimeFormatted = dayjs(parseInt(shiftedDateTime)).format(
        'YYYY-MM-DD HH:mm:ss:SSS',
      );
      setShiftedDate(shiftedDateTimeFormatted);
    }

    // No token
    if (!token) {
      const defaultRoute =
        location.pathname === routes.Registration_Affiliate
          ? routes.Affiliate_FirstLoan_Application
          : routes.FirstLoan;
      navigate(defaultRoute);
      return;
    }

    async function login(queryToken: string) {
      try {
        const { username, token } = location.pathname.includes(
          routes.Registration_Affiliate,
        )
          ? await registrationAffiliateLogin({
              body: {
                token: queryToken,
              },
            })
          : await oneTimeLogin({
              body: {
                oneTimeToken: queryToken,
              },
            });

        if (!username || !token) {
          navigate(routes.FirstLoan);
          return;
        }

        const authToken = btoa(`${username}:${token}`);
        sessionStorage.setItem('token', authToken);

        const client = await fetchClient();

        identify(
          new IdentifyEvent({
            client,
            email: client?.email?.email || 'undefined',
          }),
        );

        const application = await fetchGetApplication({
          headers: getFetchHeaders({
            Accept: 'application/vnd.4finance.web.v1.hal+json',
            'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
          }),
        });

        await sendAnalyticsCookiesIfExists({
          applicationId: application?.id,
          clientId: client?.id,
        });

        // Redirects to either vivusonline registration or account
        if (maybeRedirectCrossDomain(client, application)) {
          return;
        }

        if (affiliateInfo.provider) {
          await fetchUpdateAffiliate({
            body: affiliateInfo,
          });
        }

        if (application?.status) {
          identify(
            new IdentifyEvent({
              application,
            }),
          );
        }

        if (
          application?.affiliateCooperationType === 'API' &&
          application?.status === 'OPEN' &&
          !application?.confirmed
        ) {
          navigate(
            `${routes.Affiliate_FirstLoan_Application}${location.search ?? ''}`,
          );
          return;
        }

        if (
          await maybeNavigateByResolution({
            application,
            client,
          })
        ) {
          return;
        }

        switch (location.pathname) {
          case routes.OneTimeTokenLogin:
            navigate(routes.FirstLoan_Welcome);
            return;
          case routes.Registration_Affiliate:
            navigate(routes.Affiliate_FirstLoan_Application);
            return;
        }
        return;
      } catch (error) {
        setError(error);
      }
    }

    if (token) login(token);
  }, [
    navigate,
    searchParams,
    setError,
    location.pathname,
    maybeNavigateByResolution,
    maybeRedirectCrossDomain,
    location.search,
    reset,
    identify,
  ]);

  return (
    <div style={{ height: '100vh', display: 'flex', justifyContent: 'center' }}>
      <Loading />
    </div>
  );
}
